import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  StringField,
  IntegerField,
  BooleanField,
  SlugField,
} from '@ayocore/platform/common/helpers/schema-types';
import taskTypeSchemaDefinition from './task-type.schema';

import { name as moduleName } from '../module.json';
/**
 * Task Status Schema - Configurable statuses for task types
 *
 * Each task type can have its own workflow of statuses.
 * Examples: Todo → In Progress → Done, or Todo → Scheduled → Completed → Follow-up
 */

// Complete schema (with system fields and relations)
const TaskStatusSchema = Type.Object({
  ...SystemFields, // id, uuid, created_at, updated_at

  name: StringField({
    title: 'Статус',
    minLength: 1,
    maxLength: 100,
    description: 'Название статуса (К выполнению, В работе, Завершено, etc.)',
  }),

  slug: SlugField({
    title: 'Slug',
    description: 'Уникальный постоянный идентификатор (TODO, IN_PROGRESS, COMPLETED, etc.)',
  }),

  // ✨ Relation to task type
  task_type: RelationOne(taskTypeSchemaDefinition), // For TypeScript type
  task_type_id: RelationField(taskTypeSchemaDefinition, 'ID типа задачи', {
    onDelete: 'CASCADE',
    description: 'ID типа задачи, к которому относится этот статус',
    relationName: 'task_type',
  }),

  sort_order: IntegerField({
    title: 'Порядок',
    description: 'Порядок в рабочем процессе (0 = первый статус)',
  }),

  is_final: BooleanField({
    title: 'Финальный статус',
    default: false,
    description: 'Является ли это финальным статусом (задача завершена)',
  }),

  is_active: BooleanField({
    title: 'Активный',
    default: true,
    description: 'Доступен ли этот статус для использования',
  }),
});

// TypeScript types
export type TaskStatus = Static<typeof TaskStatusSchema>;

// ✨ CLEAN: Schema definition following our established pattern
export default defineSchema({
  moduleName,
  tableName: 'task_statuses', // System will prefix with 'tasks_'
  schema: TaskStatusSchema,
  additionalIndexes: [
    'task_type_id', // Fast lookup by task type
    'slug', // Fast lookup by slug (unique within task type)
    'sort_order', // Workflow ordering
    'is_active', // Filter active statuses
    'is_final', // Find final statuses
  ],
  foreignKeys: [
    // ✨ RelationOne auto-generates the task_type_id foreign key!
  ],
  dependencies: ['tasks_task_types'], // Need task types first
});
